import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon  from '@mui/icons-material/Clear';

/**
 * SearchableDropdown
 * قايمة منسدلة شيك مع search — بدون modal
 *
 * Props:
 *  value        - current value
 *  onChange     - fn(newValue)
 *  options      - Array<{ value, label, sublabel?, dot?: colorStr, avatarColor?: colorStr }>
 *  placeholder  - trigger placeholder
 *  isDark       - bool
 *  accentColor  - active color (default #1d4ed8)
 */
export const SearchableDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  isDark = false,
  accentColor = '#1d4ed8',
}) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* focus search when opens */
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const filtered = useMemo(() =>
    !search
      ? options
      : options.filter((o) =>
          o.label?.toLowerCase().includes(search.toLowerCase()) ||
          o.sublabel?.toLowerCase().includes(search.toLowerCase())
        ),
  [options, search]);

  const selected = options.find((o) => o.value === value);

  /* ── palette ── */
  const triggerBg  = isDark ? '#0f1117' : '#ffffff';
  const triggerBdr = isDark ? '#2a2f45' : '#e2e8f0';
  const dropBg     = isDark ? '#1e2132' : '#ffffff';
  const searchBg   = isDark ? '#161929' : '#f8fafc';
  const hoverBg    = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const txtPri     = isDark ? '#f1f5f9' : '#0f172a';
  const txtSec     = isDark ? '#94a3b8' : '#64748b';

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setSearch('');
  };

  return (
    <Box ref={wrapRef} sx={{ position: 'relative' }}>

      {/* ── Trigger button ── */}
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, height: 40, borderRadius: '8px', cursor: 'pointer',
          border: `1px solid ${open ? accentColor : (selected?.value !== '' && selected ? accentColor + '55' : triggerBdr)}`,
          backgroundColor: open || (selected?.value !== '' && selected)
            ? (isDark ? 'rgba(29,78,216,0.08)' : 'rgba(29,78,216,0.03)')
            : triggerBg,
          transition: 'all 0.15s ease',
          userSelect: 'none',
          '&:hover': { borderColor: accentColor },
        }}
      >
        {/* colored dot (for stage) */}
        {selected?.dot && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selected.dot, flexShrink: 0 }} />
        )}

        {/* mini avatar (for people) */}
        {selected?.avatarColor && !selected?.dot && (
          <Avatar sx={{ width: 20, height: 20, borderRadius: '4px', flexShrink: 0, fontSize: 9, fontWeight: 800, background: selected.avatarColor }}>
            {selected.label?.charAt(0)?.toUpperCase()}
          </Avatar>
        )}

        {/* label */}
        <Typography sx={{
          flex: 1, fontSize: 13, lineHeight: 1,
          color: (selected?.value !== '' && selected) ? (isDark ? '#93c5fd' : accentColor) : txtSec,
          fontFamily: "'DM Sans','Segoe UI',sans-serif",
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {selected?.label || placeholder}
        </Typography>

        {/* clear × */}
        {selected?.value !== '' && selected && (
          <Box
            onClick={(e) => { e.stopPropagation(); onChange(''); setSearch(''); }}
            sx={{ display: 'flex', color: txtSec, flexShrink: 0, transition: 'color 0.1s', '&:hover': { color: '#ef4444' } }}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </Box>
        )}

        {/* chevron */}
        <Box sx={{
          display: 'flex', flexShrink: 0, color: open ? accentColor : txtSec,
          transition: 'transform 0.2s, color 0.15s',
          transform: open ? 'rotate(180deg)' : 'none',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      </Box>

      {/* ── Dropdown panel ── */}
      {open && (
        <Box sx={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          minWidth: '100%',
          width: 'max-content',
          maxWidth: 260,
          zIndex: 99999,
          backgroundColor: dropBg,
          border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
          borderRadius: '12px',
          boxShadow: isDark
            ? '0 16px 48px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04)'
            : '0 16px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          animation: 'ddOpen 0.14s cubic-bezier(0.16,1,0.3,1)',
          '@keyframes ddOpen': {
            from: { opacity: 0, transform: 'translateY(-6px) scale(0.97)' },
            to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        }}>

          {/* search row */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1,
            borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f5f9'}`,
            backgroundColor: searchBg,
          }}>
            <SearchIcon sx={{ fontSize: 15, color: txtSec, flexShrink: 0 }} />
            <Box
              component="input"
              ref={inputRef}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontSize: 13, color: txtPri,
                fontFamily: "'DM Sans','Segoe UI',sans-serif",
                '&::placeholder': { color: txtSec },
              }}
            />
            {search && (
              <Box onClick={() => setSearch('')} sx={{ cursor: 'pointer', display: 'flex', color: txtSec, '&:hover': { color: txtPri } }}>
                <ClearIcon sx={{ fontSize: 13 }} />
              </Box>
            )}
          </Box>

          {/* options */}
          <Box sx={{ maxHeight: 216, overflowY: 'auto', py: 0.75 }}>
            {filtered.length === 0 ? (
              <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 12, color: txtSec }}>No results</Typography>
              </Box>
            ) : (
              filtered.map((opt) => {
                const isSel = value === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => handleSelect(opt)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      px: 1.5, py: 0.875, mx: 0.75, borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: isSel
                        ? (isDark ? 'rgba(29,78,216,0.15)' : 'rgba(29,78,216,0.07)')
                        : 'transparent',
                      transition: 'background 0.1s ease',
                      '&:hover': {
                        backgroundColor: isSel
                          ? (isDark ? 'rgba(29,78,216,0.2)' : 'rgba(29,78,216,0.1)')
                          : hoverBg,
                      },
                    }}
                  >
                    {/* dot */}
                    {opt.dot && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.dot, flexShrink: 0 }} />
                    )}

                    {/* avatar */}
                    {opt.avatarColor && !opt.dot && (
                      <Avatar sx={{ width: 24, height: 24, borderRadius: '5px', flexShrink: 0, fontSize: 10, fontWeight: 800, background: opt.avatarColor }}>
                        {opt.label?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    )}

                    {/* text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: 13, fontWeight: isSel ? 700 : 500, lineHeight: 1.3,
                        color: isSel ? (isDark ? '#93c5fd' : accentColor) : txtPri,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {opt.label}
                      </Typography>
                      {opt.sublabel && (
                        <Typography sx={{ fontSize: 11, color: txtSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opt.sublabel}
                        </Typography>
                      )}
                    </Box>

                    {/* checkmark */}
                    {isSel && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="2.5" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
